/**
 * TextField.tsx
 *
 * Unified text input field component
 * Handles: text, email, url, date, phone, zipcode, alphanumeric, password
 */

import React from 'react';
import { BaseFieldProps, BASE_INPUT_CLASSES, ERROR_INPUT_CLASSES, DISABLED_INPUT_CLASSES } from './shared/types';
import { FieldWrapper } from './shared';

export const TextField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
}) => {
  const isPreviewMode = mode === 'preview';
  const isDisabled = disabled || isPreviewMode;

  // Determine validation type
  const isPhoneField = fieldConfig.validation?.type === 'phone';
  const isPasswordField = fieldConfig.validation?.type === 'password';
  const isEmailField = fieldConfig.validation?.type === 'email';
  const isUrlField = fieldConfig.validation?.type === 'url' || fieldConfig.type === 'url';
  const isAlphanumericField = fieldConfig.validation?.type === 'alphanumeric';
  const isZipcodeField = fieldConfig.validation?.type === 'zipcode';
  const maxLength = fieldConfig.validation?.maxLength || fieldConfig.validation?.defaultMaxLength || 255;

  // Determine input type and inputMode based on validation type
  let inputType: 'text' | 'password' | 'email' | 'url' | 'date' = 'text';
  let inputMode: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' | 'search' | 'none' | undefined = 'text';

  if (isPasswordField) inputType = 'password';
  else if (isEmailField) inputType = 'email';
  else if (isUrlField) inputType = 'url';
  else if (fieldConfig.type === 'date') inputType = 'date';

  if (isPhoneField) inputMode = 'tel';
  else if (isZipcodeField) inputMode = 'numeric';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPreviewMode) return;

    const newValue = e.target.value;

    // For phone fields, allow optional leading + and digits, enforce max length 12
    // (SA format: +27712345678 = 12 chars, or 0712345678 = 10 chars)
    if (isPhoneField) {
      // Allow optional leading + followed by digits, limit to 12 characters
      if (/^\+?\d*$/.test(newValue) && newValue.length <= 12) {
        onChange(fieldConfig.id, newValue);
      }
    }
    // For zipcode fields, only allow digits and enforce max length (4 digits)
    else if (isZipcodeField) {
      // Only allow digits, limit to 4 characters for zipcode
      if (/^\d*$/.test(newValue) && newValue.length <= 4) {
        onChange(fieldConfig.id, newValue);
      }
    }
    // For alphanumeric fields, only allow letters and numbers (no special characters)
    else if (isAlphanumericField) {
      // Only allow alphanumeric characters (letters and numbers)
      if (/^[a-zA-Z0-9]*$/.test(newValue) && newValue.length <= maxLength) {
        onChange(fieldConfig.id, newValue);
      }
    }
    else {
      // For other fields, just enforce max length
      if (newValue.length <= maxLength) {
        onChange(fieldConfig.id, newValue);
      }
    }
  };

  const inputClasses = `${BASE_INPUT_CLASSES} ${hasError ? ERROR_INPUT_CLASSES : ''} ${isDisabled ? DISABLED_INPUT_CLASSES : ''}`;

  const inputElement = (
    <input
      id={fieldConfig.id}
      type={inputType}
      inputMode={inputMode}
      value={value || ''}
      onChange={handleChange}
      onFocus={(e) => {
        if (!isPreviewMode) {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }}
      maxLength={isPhoneField ? 12 : isZipcodeField ? 4 : maxLength}
      placeholder={isPreviewMode ? fieldConfig.placeholder || 'Text input' : fieldConfig.placeholder}
      disabled={isDisabled}
      className={inputClasses}
    />
  );

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={isPreviewMode}
    >
      {inputElement}
    </FieldWrapper>
  );
});

TextField.displayName = 'TextField';
